// Case studies, PLACEHOLDER COPY (clearly swappable).
// Replace `brief`, `build`, `outcome`, `metrics` and images with the real
// project write-ups and photos as they come in. Titles are the real projects.
const img = (u) => u;

export const caseStudies = [
  {
    slug: 'the-monolith',
    title: 'The Monolith',
    client: 'WOW Awards',
    category: 'Sculptural Light Landmark',
    year: '2024',
    location: 'Mumbai',
    tagline:
      'A towering light structure that became the signal point of the entire floor, the thing everyone walked toward.',
    cover: img('https://images.unsplash.com/photo-1614886205583-92fe9eaa38fb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600'),
    brief: [
      'The brief was deceptively simple: give the space a heart. A single moment that the room would organise itself around, that people would photograph without being asked, and that would still feel considered up close.',
      'The catch was scale and time. It had to read from across a vast hall, survive thousands of people moving past it, and be built, lit and struck within a punishing production window.',
    ],
    build: [
      'We engineered a freestanding vertical structure with an internal rig that let light move through it rather than just sit on it. Every section was designed to be flat-packed, numbered and assembled on site without a single custom tool.',
      'Content, structure and control were designed together in one room, so the lighting programme, the physical geometry and the safety load path were never at odds. Nothing was handed over and re-interpreted; it was solved once.',
    ],
    outcome: [
      'The Monolith became the unofficial meeting point of the show. It held its own from fifty metres and rewarded people who came close.',
      'It went up clean, ran flawlessly across the run, and came down inside the schedule, with the whole system designed to travel and be rebuilt.',
    ],
    metrics: [
      { value: 'Full run', label: 'Zero downtime' },
      { value: '1 build', label: 'Flat-packed & re-usable' },
      { value: 'Landmark', label: 'Floor centrepiece' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1570005616859-784c0e1644c5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
      'https://images.unsplash.com/photo-1662318615953-ea4938514ac9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
      'https://images.unsplash.com/photo-1670270488206-5f83652c0095?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
    ],
  },
  {
    slug: 'the-chandelier',
    title: 'The Chandelier',
    client: 'WOW Awards',
    category: 'Kinetic Light Installation',
    year: '2023',
    location: 'Mumbai',
    tagline:
      'Hundreds of individually controlled points of light, choreographed to breathe, ripple and fall silent on cue.',
    cover: img('https://images.unsplash.com/photo-1598155038124-0ae4d7300461?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600'),
    brief: [
      'We were asked for a ceiling moment with grandeur, but one that could perform, not just hang there. It needed to feel handcrafted and precise while being fully programmable.',
      'The overhead load, the sightlines and the control had to be resolved without visible clutter, above a crowd, in a heritage-sensitive space.',
    ],
    build: [
      'We designed a suspended matrix of individually addressable elements on a concealed rigging plan, with the control system and the fixing strategy engineered in parallel with the creative choreography.',
      'A single show file drove the whole piece, so the movement, the light and the music read as one gesture instead of three systems trying to sync.',
    ],
    outcome: [
      'The Chandelier turned the ceiling into a performer. It could sit still and elegant, then bloom into motion for the key moments of the evening.',
      'It became the image the event was remembered by, and the rig was designed to be re-hung for future shows.',
    ],
    metrics: [
      { value: 'Programmable', label: 'Choreographed to cue' },
      { value: 'Concealed', label: 'Clean rigging plan' },
      { value: 'Signature', label: 'Hero moment' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1711102661247-ee734904b6de?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
      'https://images.unsplash.com/photo-1627306411131-358d6d0fd2cb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
      'https://images.unsplash.com/photo-1572955034096-233ea61a78d8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
    ],
  },
  {
    slug: 'maritime-experience-centre',
    title: 'Maritime Experience Centre',
    client: 'Confidential',
    category: 'Heritage & Storytelling',
    year: '2023',
    location: 'India',
    tagline:
      'Turning a nation’s relationship with the sea into a walk-through story of objects, light, sound and space.',
    cover: img('https://images.unsplash.com/photo-1761091898718-6129b5b5e0ad?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600'),
    brief: [
      'The task was to make a large body of history feel personal, to move visitors from “reading about it” to “standing inside it”, without diluting the facts.',
      'It had to work for school groups and specialists on the same day, and hold up over years of daily footfall.',
    ],
    build: [
      'We built the visitor journey first, the emotional arc, and then engineered the media, lighting and spatial design to serve that arc scene by scene.',
      'Interactives, projection and physical artefacts were planned as one system, so technology never felt bolted on and maintenance was designed in from day one.',
    ],
    outcome: [
      'Visitors move through the story rather than around exhibits, and remember moments rather than plaques.',
      'The centre runs as a durable, serviceable system built for the long term, not a one-night installation.',
    ],
    metrics: [
      { value: 'Full journey', label: 'Narrative-led design' },
      { value: 'All-day', label: 'Built for footfall' },
      { value: 'Durable', label: 'Serviceable systems' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1672383733733-6d3fbf73c518?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
      'https://images.unsplash.com/photo-1529682668343-870962b235a2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
      'https://images.unsplash.com/photo-1770739527434-aa23c732ff1b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
    ],
  },
  {
    slug: 'the-immersion',
    title: 'The Immersion',
    client: 'Confidential',
    category: 'Projection & Digital Worlds',
    year: '2024',
    location: 'Mumbai',
    tagline:
      'A fully surrounding brand world where the floor, walls and people all became part of the same living canvas.',
    cover: img('https://images.unsplash.com/photo-1568952433726-3896e3881c65?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600'),
    brief: [
      'The brand wanted more than a screen. They wanted people to step inside the idea, to be surrounded, not shown.',
      'It had to be beautiful, but also robust: precise projection, reliable tracking, and content that never stuttered in front of an audience.',
    ],
    build: [
      'We designed the space, the projection geometry and the content pipeline together, calibrating every surface so the imagery wrapped the room seamlessly.',
      'The technical strategy, servers, mapping, sensors and failovers, was engineered so the magic on the surface was backed by something dependable underneath.',
    ],
    outcome: [
      'People stopped watching and started participating. The experience became the story they told afterwards.',
      'It ran reliably across long show days, with the whole system planned to be re-deployed at other venues.',
    ],
    metrics: [
      { value: '360°', label: 'Fully surrounding' },
      { value: 'Real-time', label: 'Responsive to people' },
      { value: 'Tourable', label: 'Re-deployable rig' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1533144169699-97ec15a75e92?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
      'https://images.unsplash.com/photo-1593073862407-a3ce22748763?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
      'https://images.unsplash.com/photo-1604355714851-c1d5990e1696?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
    ],
  },
  {
    slug: 'flagship-experience',
    title: 'Flagship Experience',
    client: 'Confidential',
    category: 'Experiential Retail',
    year: '2022',
    location: 'India',
    tagline:
      'A retail space designed as an experience first and a store second, where the architecture does the selling.',
    cover: img('https://images.unsplash.com/photo-1667746213225-431db3ed8d13?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600'),
    brief: [
      'The client didn’t want another shop. They wanted a destination, a space that people would travel to, spend time in, and feel changed the way they saw the brand.',
      'It had to be striking on opening day and still make commercial sense every day after.',
    ],
    build: [
      'We treated the store as a stage: sculptural architecture, considered light, and quiet technology working together to guide people through a deliberate journey.',
      'Every experiential idea was pressure-tested against operations, maintenance and footfall so the wow factor could survive real trading conditions.',
    ],
    outcome: [
      'The flagship became a reason to visit, not just a place to buy, a physical expression of the brand people wanted to be photographed in.',
      'It balanced spectacle with the practical realities of running a retail floor every single day.',
    ],
    metrics: [
      { value: 'Destination', label: 'A reason to visit' },
      { value: 'Considered', label: 'Journey-led layout' },
      { value: 'Everyday', label: 'Built to trade' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1526745925052-dd824d27b9ab?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
      'https://images.unsplash.com/photo-1567958436049-f2903793328b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
      'https://images.unsplash.com/photo-1570857502809-08184874388e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
    ],
  },
  {
    slug: 'the-grand-reveal',
    title: 'The Grand Reveal',
    client: 'Confidential',
    category: 'Live Show & Reveal',
    year: '2024',
    location: 'India',
    tagline:
      'A single, breath-held moment of reveal, engineered so that light, sound, motion and timing land as one.',
    cover: img('https://images.unsplash.com/photo-1558620013-a08999547a36?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600'),
    brief: [
      'A launch lives or dies on one moment. The brief was to design that moment, the reveal, and everything that builds tension toward it.',
      'It had to be flawless on the first and only take, in front of a live audience and a hundred cameras, with no room for a second attempt.',
    ],
    build: [
      'We choreographed the show as a single timeline: lighting, audio, automation and the physical reveal cued together and rehearsed until the timing was invisible.',
      'The technical strategy, show control, redundancy and a clear fail-safe plan, was built so the spectacle never depended on luck.',
    ],
    outcome: [
      'The reveal landed exactly as designed, and the room reacted the way great moments make people react, all at once.',
      'The show ran clean, on cue, and gave the brand the single image the whole campaign was built around.',
    ],
    metrics: [
      { value: 'One take', label: 'Live, no retries' },
      { value: 'Synced', label: 'Light · sound · motion' },
      { value: 'Fail-safe', label: 'Redundant show control' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1558970439-add78fc68990?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
      'https://images.unsplash.com/photo-1593073862407-a3ce22748763?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
      'https://images.unsplash.com/photo-1568952433726-3896e3881c65?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
    ],
  },
];

export const getCaseStudy = (slug) => caseStudies.find((c) => c.slug === slug);
